import { computed, Injectable, signal, WritableSignal } from '@angular/core'
import { isEqual, orderBy } from 'lodash'
import { EditableRow, ValidationMessage } from './entity-metadata'

export type ChangeSetDescriptor = Readonly<{
  url: string
  description: string
}>

export type ChangeSet<T = unknown> = ChangeSetDescriptor & Readonly<{
  timestamp: number
  changes: readonly EditableRow<T>[]
  validationMessages: readonly ValidationMessage[]
}>

type ChangeSetMap = Readonly<Record<string, WritableSignal<ChangeSet>>>

@Injectable({ providedIn: 'root' })
export class EntityManagementChangesService {
  private readonly changeSetMap = signal<ChangeSetMap>({})

  readonly changeSets = computed((): readonly ChangeSet[] =>
    orderBy(Object.values(this.changeSetMap()).map(signal => signal()), 'timestamp', 'desc'),
  )

  initChangeSet(descriptor: ChangeSetDescriptor) {
    const changeSetSignal = this.changeSetMap()[descriptor.url]
    if (!changeSetSignal) {
      this.changeSetMap.set({
        ...this.changeSetMap(),
        [descriptor.url]: signal<ChangeSet>({
          ...descriptor,
          timestamp: Date.now(),
          changes: [],
          validationMessages: [],
        }),
      })
    }
  }

  private getChangeSet<T>(url: string): WritableSignal<ChangeSet<T>> | undefined {
    return this.changeSetMap()[url] as WritableSignal<ChangeSet<T>> | undefined
  }

  private getRequiredChangeSet<T>(url: string): WritableSignal<ChangeSet<T>> {
    const changeSet = this.getChangeSet<T>(url)
    if (changeSet) {
      return changeSet
    } else {
      throw new Error(`Change set not found: ${url}`)
    }
  }

  getChanges<T>(url: string): readonly EditableRow<T>[] {
    return this.getChangeSet<T>(url)?.().changes ?? []
  }

  getValidationMessages<T>(url: string): readonly ValidationMessage[] {
    return this.getChangeSet<T>(url)?.().validationMessages ?? []
  }

  updateChanges<T>(url: string, update: (old: readonly EditableRow<T>[]) => readonly EditableRow<T>[]) {
    const changeSet = this.getRequiredChangeSet<T>(url)
    const { changes } = changeSet()
    const newChanges = update(changes)
    if (!isEqual(newChanges, changes)) {
      changeSet.set({ ...changeSet(), timestamp: Date.now(), changes: newChanges })
    }
  }

  setValidationMessages(url: string, messages: readonly ValidationMessage[]) {
    const changeSet = this.getRequiredChangeSet(url)
    changeSet.set({ ...changeSet(), validationMessages: messages })
  }

  undoAll(url: string) {
    const changeSet = this.getChangeSet(url)
    if (changeSet) {
      changeSet.set({ ...changeSet(), changes: [], validationMessages: [] })
      this.updateChanges(url, () => [])
      this.setValidationMessages(url, [])
    }
  }
}
